import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { AuthApi, CustomerApi, MainApi, xVerifyApi } from '_api';
import { STORAGE_KEYS } from '_constants/storage';
import { handleError, handleResponse } from '_helpers/api';
import { debounce } from '_helpers/common';
import {
  clearLocalValue,
  clearSessionValue,
  restoreLocalValue,
  storeLocalValue,
  storeSessionValue,
} from '_helpers/storage';

import { resetActiveOrderId } from '_store/order/slice';
import { resetData as resetActiveOrderData } from '_store/order/slice';

export const resetPassword = createAsyncThunk(
  'user/resetPassword',
  async function ({ email, category }, { rejectWithValue }) {
    try {
      const response = await CustomerApi.resetPassword({ email, category });
      const result = handleResponse(response);
      return result;
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const setPassword = createAsyncThunk(
  'user/setPassword',
  async function ({ token, password, category }, { rejectWithValue }) {
    try {
      const response = await CustomerApi.setPassword({ token, password, category });
      const result = handleResponse(response);
      return result;
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const login = createAsyncThunk(
  'user/login',
  async function ({ username, password, category }, { dispatch, rejectWithValue }) {
    try {
      const response = await AuthApi.login({ username, password, category });
      const result = handleResponse(response);
      const { token } = result || {};

      CustomerApi.Authorization = `Bearer ${token}`;
      MainApi.Authorization = `Bearer ${token}`;
      xVerifyApi.Authorization = `Bearer ${token}`;

      dispatch(getMe());

      debounce(() => {
        storeLocalValue(STORAGE_KEYS.accessToken, token);
      })();
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const loginByToken = createAsyncThunk(
  'user/loginByToken',
  async function ({ token, orderId }, { dispatch, rejectWithValue }) {
    try {
      storeLocalValue(STORAGE_KEYS.accessToken, token);
      orderId && storeSessionValue(STORAGE_KEYS.activeOrderId, orderId);
      orderId && storeSessionValue(STORAGE_KEYS.loginFromCRM, 'true');
      CustomerApi.Authorization = `Bearer ${token}`;
      MainApi.Authorization = `Bearer ${token}`;
      xVerifyApi.Authorization = `Bearer ${token}`;
      dispatch(getMe());
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const resetStore = createAsyncThunk('user/resetStore', async function (_, { dispatch }) {
  CustomerApi.Authorization = null;
  MainApi.Authorization = null;
  xVerifyApi.Authorization = null;

  dispatch(resetDetails());
  dispatch(resetActiveOrderId());
  dispatch(resetActiveOrderData());
});

export const logout = createAsyncThunk('user/logout', async function (_, { dispatch }) {
  CustomerApi.Authorization = null;
  MainApi.Authorization = null;
  xVerifyApi.Authorization = null;

  dispatch(resetDetails());
  dispatch(resetActiveOrderId());
  dispatch(resetActiveOrderData());

  debounce(() => {
    CustomerApi.cancelAll();
    clearLocalValue(STORAGE_KEYS.accessToken);
    clearLocalValue(STORAGE_KEYS.userInfo);
    clearSessionValue(STORAGE_KEYS.activeOrderId);
    clearSessionValue(STORAGE_KEYS.loginFromCRM);
  })();
});

export const getMe = createAsyncThunk('user/getMe', async function (_, { rejectWithValue }) {
  try {
    const response = await CustomerApi.getMe();
    const result = handleResponse(response);
    storeLocalValue(STORAGE_KEYS.userInfo, result);
    return result;
  } catch (error) {
    const processedError = handleError(error);
    return rejectWithValue(processedError);
  }
});

export const updateMe = createAsyncThunk(
  'user/updateMe',
  async function ({ customerId, body }, { dispatch, getState, rejectWithValue }) {
    try {
      const { user } = getState();
      // eslint-disable-next-line no-unused-vars
      const { uid: _, ...currentDetails } = user.details || {};
      const newDetails = body || {};

      await CustomerApi.updateMe({ customerId, body: { ...currentDetails, ...newDetails } });
      dispatch(getMe());
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const changePassword = createAsyncThunk(
  'user/changePassword',
  async function ({ customerId, body }, { dispatch, getState, rejectWithValue }) {
    try {
      const { user } = getState();
      // eslint-disable-next-line no-unused-vars
      const { uid: _, ...currentDetails } = user.details || {};
      const newDetails = body || {};

      await CustomerApi.updateMe({ customerId, body: { ...currentDetails, ...newDetails } });
      toast.success('Password was changed successfully!');
      dispatch(getMe());
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState: {
    details: null,
    error: null,
    loading: true,
    updating: false,
    loggedIn: false,
    passwordResetStatus: false,
    passwordSetStatus: false,
    changingPassword: false,
  },
  reducers: {
    restoreDetails(state) {
      const restoredUserInfo = restoreLocalValue(STORAGE_KEYS.userInfo);
      state.details = restoredUserInfo;
      state.loggedIn = !!restoredUserInfo;
      state.loading = false;
    },
    resetDetails(state) {
      state.details = null;
      state.loggedIn = false;
      state.loading = false;
    },
    resetError(state) {
      state.error = null;
    },
    resetPasswordResetStatus(state) {
      state.passwordResetStatus = false;
    },
    resetPasswordSetStatus(state) {
      state.passwordSetStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loggedIn = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload;
        state.loggedIn = false;
        state.loading = false;
      });

    builder
      .addCase(getMe.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.details = action.payload;
        state.loggedIn = true;
        state.loading = false;
      })
      .addCase(getMe.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });

    builder
      .addCase(updateMe.pending, (state) => {
        state.error = null;
        state.updating = true;
      })
      .addCase(updateMe.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(updateMe.rejected, (state, action) => {
        state.error = action.payload;
        state.updating = false;
      });

    builder
      .addCase(changePassword.pending, (state) => {
        state.error = null;
        state.changingPassword = true;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.changingPassword = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.error = action.payload;
        state.changingPassword = false;
      });

    builder
      .addCase(resetPassword.pending, (state) => {
        state.error = null;
        state.passwordResetStatus = false;
        state.loading = true;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.passwordResetStatus = true;
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.passwordResetStatus = false;
        state.error = action.payload;
        state.loading = false;
      });

    builder
      .addCase(setPassword.pending, (state) => {
        state.error = null;
        state.passwordSetStatus = false;
        state.loading = true;
      })
      .addCase(setPassword.fulfilled, (state) => {
        state.passwordSetStatus = true;
        state.loading = false;
      })
      .addCase(setPassword.rejected, (state, action) => {
        state.error = action.payload;
        state.passwordSetStatus = false;
        state.loading = false;
      });
  },
});

export const { restoreDetails, resetDetails, resetError, resetPasswordResetStatus, resetPasswordSetStatus } =
  userSlice.actions;

export default userSlice.reducer;
